import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import studentGroupApi from "@apis/student.group.api.ts";
import { NAMESPACE, RESOURCE_DOMAIN } from "@modules/editor/service/editor.constant.ts";
import { ROLE_IN_GROUP } from "@modules/student-group/service/student.group.constant.ts";
import { StudentGroupTeacherList } from "@modules/student-group/student-group-types.ts";
import { MentionableUser } from "@plugins/richtext/plugins/mentions/types.ts";
import { isEmpty } from "@utils/var.check";
import { OwnerGroupMentionRecipientModel } from "@modules/attempt/types.ts";
import ownerGroupApi from "@apis/owner.group.api.ts";

const caCoRecipientMap = new Map<string, OwnerGroupMentionRecipientModel[]>();
const teacherRecipientMap = new Map<string, StudentGroupTeacherList[]>();

const getCaCoRecipient = async (lessonUuid: string): Promise<OwnerGroupMentionRecipientModel[]> => {
  const recipients = caCoRecipientMap.get(lessonUuid);
  if (recipients) {
    return recipients;
  }

  const caCoRecipients = await loadCaCoRecipients(lessonUuid);
  caCoRecipientMap.set(lessonUuid, caCoRecipients);
  return caCoRecipients;
};

const getTeacherRecipient = async (
  lessonUuid: string,
  studentGroupCollectionUuid: string,
): Promise<StudentGroupTeacherList[]> => {
  const recipients = teacherRecipientMap.get(lessonUuid);
  if (recipients) {
    return recipients;
  }

  const teacherRecipients = await loadTeacherRecipientsByCollectionUuid(studentGroupCollectionUuid);
  teacherRecipientMap.set(lessonUuid, teacherRecipients);
  return teacherRecipients;
};

const getUserId = (userId: string | number | null | undefined): number => {
  const normalizedUserId = Number(userId);
  return Number.isNaN(normalizedUserId) ? -1 : normalizedUserId;
};

const toTeacherMention = (
  recipient: OwnerGroupMentionRecipientModel | StudentGroupTeacherList,
  roleLabel: string,
  aggregateLabel: string,
): MentionableUser => ({
  ...recipient,
  name: recipient.formattedUserName ?? recipient?.name ?? "", // replace name field with formattedUserName for mentions/plugin.ts to use
  isTeacher: true,
  roleLabel,
  aggregateLabel,
});

const loadTeacherRecipientsByCollectionUuid = async (
  collectionUuid: string,
): Promise<StudentGroupTeacherList[]> => {
  const response = await studentGroupApi.fetchStudentGroupTeacher(collectionUuid);

  if (isEmpty(response) || isEmpty(response?.data)) {
    return [];
  }

  return response.data;
};

const loadCaCoRecipients = async (
  lessonUuid: string,
): Promise<OwnerGroupMentionRecipientModel[]> => {
  const response = await ownerGroupApi.fetchOwnViewMentionRecipients({ lessonUuid });

  if (response.code !== "ok") {
    return [];
  }

  return response.data;
};

export function useLessonOwnViewMentions() {
  const store = useStore();
  const { t } = useI18n();

  const lesson = computed(() => store.getters[`${NAMESPACE.LESSON}/getLesson`]);
  const studentGroupCollectionUuid = computed<string>(
    () => store.getters[`${NAMESPACE.LESSON}/getStudentGroupCollectionUuid`] ?? "",
  );
  const authUserId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);

  const getLessonOwnViewMentionableTeacherList = async (): Promise<MentionableUser[]> => {
    const currentUserId = getUserId(authUserId.value);
    const lessonUuid = lesson.value?.uuid;
    if (isEmpty(lessonUuid) || isEmpty(lesson.value?.resourceDomain)) {
      console.error("lesson uuid or lesson resourceDomain is empty.");
      return [];
    }

    // for MOE admin lesson
    if (lesson.value?.resourceDomain === RESOURCE_DOMAIN.MOE.value) {
      const recipients = await getCaCoRecipient(lessonUuid);

      return recipients
        .filter((recipient) => recipient.userId !== currentUserId)
        .map((recipient) =>
          toTeacherMention(recipient, t("mention_role_caco_label"), t("mention_all_caco_label")),
        );
    } else {
      // for class group resource
      const recipients = await getTeacherRecipient(lessonUuid, studentGroupCollectionUuid.value);

      return recipients
        .filter(
          (recipient) =>
            recipient.userId !== currentUserId &&
            recipient.roleInGroup !== ROLE_IN_GROUP.LIAISON_TEACHER,
        )
        .map((recipient) =>
          toTeacherMention(
            recipient,
            t("app_side_nav_role_name_teacher"),
            t("announcement_receiver_all_teachers_label"),
          ),
        );
    }
  };

  const getLessonOwnViewMentionableStudentList = async (): Promise<MentionableUser[]> => {
    return []; // return empty because there is no mentionable student list for lesson own view
  };

  return {
    getLessonOwnViewMentionableTeacherList,
    getLessonOwnViewMentionableStudentList,
  };
}
